import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Mic2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AuthDialog from '@/components/auth/AuthDialog';
import { useNostrLogin } from '@nostrify/react/login';

const AVATAR_URL =
  'https://pfp.nostr.build/c878259e8c88bef7efb72c0393297c6d45e41f209c4120fc7916ec19fbd66610.gif';

const BANNER_URL =
  'https://profilepics.nostur.com/banner_v1/15a5de4f827617379d6811dd75f2860aa121cdd42293bfe2f736567304c05e7d/banner.jpg?1753726432';

export default function Landing() {
  const [authOpen, setAuthOpen] = useState(false);
  // useNostrLogin is synchronous — reads straight from localStorage, no async wait
  const { logins } = useNostrLogin();
  const navigate = useNavigate();

  // As soon as a login exists, go to the episodes page
  useEffect(() => {
    if (logins.length > 0) {
      navigate('/episodes', { replace: true });
    }
  }, [logins, navigate]);

  useSeoMeta({
    title: 'Simply Nostr — The People of Nostr',
    description: 'Conversations with the people building, using, and shaping the Nostr ecosystem.',
    ogImage: BANNER_URL,
  });

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Background */}
      <div className="fixed inset-0 -z-10">
        <img
          src={BANNER_URL}
          alt=""
          className="w-full h-full object-cover opacity-10 blur-sm scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
      </div>

      {/* Main content — centered vertically */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-16 text-center">
        {/* Avatar */}
        <div className="mb-6 w-24 h-24 rounded-full overflow-hidden ring-4 ring-orange-500/40 shadow-2xl shadow-orange-500/20">
          <img
            src={AVATAR_URL}
            alt="Simply Nostr"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Logo text */}
        <h1 className="text-5xl sm:text-6xl font-black tracking-tight mb-3">
          Simply<span className="text-orange-500">Nostr</span>
        </h1>

        {/* Tagline */}
        <p className="text-lg sm:text-xl text-muted-foreground max-w-sm mb-2">
          The people of Nostr
        </p>
        <p className="text-sm text-muted-foreground/70 max-w-xs mb-10">
          Conversations with the builders, thinkers, and nostriches shaping the future of the open web.
        </p>

        {/* Login button */}
        <Button
          size="lg"
          onClick={() => setAuthOpen(true)}
          className="rounded-full px-10 py-6 text-base font-semibold bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-all"
        >
          <Mic2 className="w-5 h-5 mr-2" />
          Login with Nostr
        </Button>

        <p className="mt-4 text-xs text-muted-foreground/60">
          Use your nsec key, browser extension, or remote signer
        </p>
      </div>

      {/* Footer */}
      <footer className="py-6 text-center text-xs text-muted-foreground/50">
        Vibed with{' '}
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-purple-400/70 hover:text-purple-400 transition-colors"
        >
          Shakespeare
        </a>
      </footer>

      <AuthDialog isOpen={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}
